import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import { useSearchProducts } from "../../../../client/hooks/products/products.queries";
import { useUpdateProductPrice } from "../../../../client/hooks/products/products.mutations";

const CatalogContext = createContext();

export const useCatalogContext = () => {
    const context = useContext(CatalogContext);
    if (!context) {
        throw new Error("useCatalogContext deve ser usado dentro de um CatalogProvider");
    }
    return context;
};

export const CatalogProvider = ({ children }) => {
    // Estados
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(30); // default agora é 30
    const [alert, setAlert] = useState(false);
    const [message, setMessage] = useState("");
    const [severity, setSeverity] = useState("info");
    const [searchEnabled, setSearchEnabled] = useState(true);
    const [viewMode, setViewMode] = useState('grid');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [currentSort, setCurrentSort] = useState('relevance');
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [batchActionAnchorEl, setBatchActionAnchorEl] = useState(null);
    const [additionalMenuAnchorEl, setAdditionalMenuAnchorEl] = useState(null);    const [actionDialogOpen, setActionDialogOpen] = useState(false);
    const [currentAction, setCurrentAction] = useState(null);
    const [sortAnchorEl, setSortAnchorEl] = useState(null);
    const [cmvImportDialogOpen, setCmvImportDialogOpen] = useState(false);
    const [filters, setFilters] = useState({
        nome: "",
        codigo: "",
        categoria: "",
        precoMin: "",
        precoMax: "",
        status: "todos",
        estoque: "todos",
    });

    const [searchParams, setSearchParams] = useState({
        channel: "MERCADO_LIVRE",
        limit: 12,
        page: 1,
    });

    const [editingPriceProductId, setEditingPriceProductId] = useState(null);
    const [editingPriceSkuId, setEditingPriceSkuId] = useState(null);
    const [newPrice, setNewPrice] = useState('');

    // Estado para índice do carrossel de imagens por produto
    const [carouselImageIndexes, setCarouselImageIndexes] = useState({});

    // Queries e Mutations
    const {
        data: productsData,
        isLoading: loading,
        isError,
        error: errorData,
        refetch
    } = useSearchProducts(searchParams, searchEnabled);

    const {
        mutate: updateProductPrice,
        isLoading: priceUpdateLoading
    } = useUpdateProductPrice();

    // Dados derivados
    const produtos = productsData?.products || [];
    const totalCount = productsData?.pagination.total || 0;

    // Efeitos
    useEffect(() => {
        if (isError && errorData) {
            setAlert(true);
            setMessage(errorData?.response?.data?.message || "Erro ao buscar produtos");
            setSeverity("error");
        }
    }, [isError, errorData]);

    useEffect(() => {
        // Converte a página baseada em zero para baseada em um para a API
        setSearchParams(prev => ({
            ...prev,
            page: page + 1,
            limit: rowsPerPage
        }));
    }, [page, rowsPerPage]);

    useEffect(() => {
        if (searchEnabled) {
            refetch();
        }
    }, [searchParams.page, searchParams.limit, refetch, searchEnabled]);

    // Funções auxiliares
    const formatPrice = (price) => {
        if (!price && price !== 0) return '-';
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2
        }).format(price);
    };

    const calculatePriceDifference = (meliPrice, marketPrice) => {
        if (!meliPrice || !marketPrice || meliPrice <= 0 || marketPrice <= 0) {
            return null;
        }
        const diffPercentage = ((meliPrice / marketPrice) - 1) * 100;
        return diffPercentage.toFixed(1);
    };

    // Handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleFilterChange = (e) => {
        // Corrige: categoria agora é sempre string (id), sem JSON.parse
        setFilters({
            ...filters,
            [e.target.name]: e.target.value,
        });
    };

    const handleSearch = () => {
        setPage(0);
        const params = {
            channel: "MERCADO_LIVRE",
            limit: rowsPerPage,
            page: 1,
            // sort: currentSort
        };

        if (filters.nome) params.title = filters.nome;
        if (filters.codigo) params.partnerId = filters.codigo;
        if (filters.categoria && filters.categoria !== "") params.category_id = filters.categoria;
        if (filters.precoMin && !isNaN(parseFloat(filters.precoMin))) params.minPrice = parseFloat(filters.precoMin);
        if (filters.precoMax && !isNaN(parseFloat(filters.precoMax))) params.maxPrice = parseFloat(filters.precoMax);
        if (filters.status && filters.status !== "todos") params.isActive = filters.status === "ativo";

        if (filters.estoque && filters.estoque !== "todos") {
            switch (filters.estoque) {
                case "disponivel":
                    params.minStock = 10;
                    break;
                case "baixo":
                    params.maxStock = 10;
                    params.minStock = 1;
                    break;
                case "indisponivel":
                    params.maxStock = 0;
                    break;
            }
        }

        setSearchParams(params);
        setSearchEnabled(true);

        if (drawerOpen) {
            setDrawerOpen(false);
        }
    };

    const handleViewModeChange = (event, newViewMode) => {
        if (newViewMode !== null) {
            setViewMode(newViewMode);
        }
    };

    const handleResetFilters = () => {
        setFilters({
            nome: "",
            codigo: "",
            categoria: "",
            precoMin: "",
            precoMax: "",
            status: "todos",
            estoque: "todos",
        });
    };

    // Seleção de SKUs: aceita array de partnerIds
    const handleProductSelection = (event, skuIds) => {
        let ids = Array.isArray(skuIds) ? skuIds : [skuIds];
        if (event.target.checked) {
            setSelectedProducts(prev => Array.from(new Set([...prev, ...ids])));
        } else {
            setSelectedProducts(prev => prev.filter(id => !ids.includes(id)));
        }
    };

    const handleClearSelections = () => {
        setSelectedProducts([]);
    };

    const handleBatchAction = (action) => {
        setBatchActionAnchorEl(null);
        setCurrentAction(action);
        setActionDialogOpen(true);
    };    const handleCreateProduto = () => {
        setAlert(true);
        setMessage("Funcionalidade de criação em desenvolvimento");
        setSeverity("info");
    };
    
    const handleImportCmv = () => {
        setCmvImportDialogOpen(true);
    };

    const handleToggleProductActive = (produto) => {
        setAlert(true);
        setMessage(`Status do produto ${produto.isProductActive ? 'desativado' : 'ativado'} com sucesso!`);
        setSeverity("success");
    };

    const handleStartEditPrice = (produto) => {
        if (!produto.skus || !produto.skus[0]) {
            setAlert(true);
            setMessage("Produto não possui SKU para editar preço");
            setSeverity("error");
            return;
        }

        setEditingPriceProductId(produto.id);
        setEditingPriceSkuId(produto.skus[0].partnerId);
        setNewPrice(produto.skus[0].price?.toString() || '');
    };

    const handleSavePrice = () => {
        // Converte vírgula para ponto antes do parseFloat
        const normalizedPrice = newPrice.replace(',', '.');
        if (!normalizedPrice || isNaN(parseFloat(normalizedPrice)) || parseFloat(normalizedPrice) < 0) {
            setAlert(true);
            setMessage("Por favor, insira um preço válido");
            setSeverity("error");
            return;
        }

        updateProductPrice(
            {
                data: [
                    {
                        sku: editingPriceSkuId,
                        cmv_price: parseFloat(newPrice.replace(',', '.'))
                    }
                ]
            },
            {
                onSuccess: () => {
                    setAlert(true);
                    setMessage("Preço atualizado com sucesso!");
                    setSeverity("success");
                    setEditingPriceProductId(null);
                    setEditingPriceSkuId(null);
                    refetch();
                },
                onError: (error) => {
                    setAlert(true);
                    setMessage(error?.response?.data?.message || "Erro ao atualizar preço");
                    setSeverity("error");
                }
            }
        );
    };

    const handleCancelEditPrice = () => {
        setEditingPriceProductId(null);
        setEditingPriceSkuId(null);
        setNewPrice('');
    };

    const handleSortMenuOpen = (event) => {
        setSortAnchorEl(event.currentTarget);
    };

    const handleSortMenuClose = () => {
        setSortAnchorEl(null);
    };

    const handleSortChange = (sortOption) => {
        setCurrentSort(sortOption);
        setSearchParams(prev => ({
            ...prev,
            sort: sortOption
        }));
        handleSortMenuClose();
    };

    const handleDrawerToggle = () => {
        setDrawerOpen(!drawerOpen);
    };

    const handleAdditionalMenuOpen = (event) => {
        setAdditionalMenuAnchorEl(event.currentTarget);
    };

    const handleAdditionalMenuClose = () => {
        setAdditionalMenuAnchorEl(null);
    };

    // Retorna o índice atual do carrossel para um produto
    const getProductImageIndex = (productId) => {
        return carouselImageIndexes[productId] || 0;
    };

    // Atualiza o índice do carrossel para um produto
    const setProductImageIndex = (productId, index) => {
        setCarouselImageIndexes(prev => ({
            ...prev,
            [productId]: index
        }));
    };

    // Opções de paginação
    const rowsPerPageOptions = [30, 50, 100];

    // Contexto compartilhado
    const value = useMemo(() => ({
        // Estados
        page,
        rowsPerPage,
        alert,
        message,
        severity,
        viewMode,
        drawerOpen,
        currentSort,
        selectedProducts,
        batchActionAnchorEl,        additionalMenuAnchorEl,
        actionDialogOpen,
        currentAction,
        cmvImportDialogOpen,
        filters,
        produtos,
        totalCount,
        loading,
        editingPriceProductId,
        editingPriceSkuId,
        newPrice,
        priceUpdateLoading,
        sortAnchorEl,
        carouselImageIndexes,
        getProductImageIndex,
        setProductImageIndex,
        // Setters        setAlert,
        setActionDialogOpen,
        setBatchActionAnchorEl,
        setCmvImportDialogOpen,
        setNewPrice,
        setMessage,
        setSeverity,
        setSelectedProducts,
        setRowsPerPage,
        // Funções
        formatPrice,
        calculatePriceDifference,
        handleChangePage,
        handleChangeRowsPerPage,
        handleFilterChange,
        handleSearch,
        handleResetFilters,
        handleViewModeChange,
        handleProductSelection,
        handleClearSelections,
        handleBatchAction,        handleCreateProduto,
        handleImportCmv,
        handleToggleProductActive,
        handleStartEditPrice,
        handleSavePrice,
        handleCancelEditPrice,
        handleDrawerToggle,
        handleSortMenuOpen,        handleSortMenuClose,
        handleSortChange,
        handleAdditionalMenuOpen,
        handleAdditionalMenuClose,
        refetch,
        updateProductPrice}), [
        page, rowsPerPage, alert, message, severity, viewMode, drawerOpen, currentSort, selectedProducts, batchActionAnchorEl, additionalMenuAnchorEl, actionDialogOpen, currentAction, cmvImportDialogOpen, filters, produtos, totalCount, loading, editingPriceProductId, editingPriceSkuId, newPrice, priceUpdateLoading, sortAnchorEl, carouselImageIndexes,
        getProductImageIndex, setProductImageIndex, setAlert, setActionDialogOpen, setBatchActionAnchorEl, setNewPrice, setMessage, setSeverity, setSelectedProducts, setRowsPerPage, rowsPerPageOptions,
        formatPrice, calculatePriceDifference, handleChangePage, handleChangeRowsPerPage, handleFilterChange, handleSearch, handleResetFilters, handleViewModeChange, handleProductSelection, handleClearSelections, handleBatchAction, handleCreateProduto, handleImportCmv, handleToggleProductActive, handleStartEditPrice, handleSavePrice, handleCancelEditPrice, handleDrawerToggle, handleSortMenuOpen, handleSortMenuClose, handleSortChange, handleAdditionalMenuOpen, handleAdditionalMenuClose, refetch, updateProductPrice
    ]);

    return (
        <CatalogContext.Provider value={value}>
            {children}
        </CatalogContext.Provider>
    );
};

export default CatalogContext;

// Para aumentar apenas a área de exibição dos produtos (grid/lista),
// reverta o tamanho dos cards/lista para o original e aumente só o valor mínimo de altura nos componentes de grid/lista.
// Não é necessário alterar nada no CatalogProvider para isso.
